import React, { useState, useEffect } from 'react';
import {
  Box,
  VStack,
  HStack,
  Text,
  Button,
  Input,
  useToast,
  Divider,
  Heading,
  Spinner,
  useColorModeValue,
  Card,
  CardBody,
  Icon,
  Flex,
  Badge,
  InputGroup,
  InputLeftElement,
  SimpleGrid,
  Center,
} from '@chakra-ui/react';
import { FiPhone, FiPlus, FiSearch, FiRefreshCw } from 'react-icons/fi';

const PhoneNumbers = () => {
  const [phoneNumbers, setPhoneNumbers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [inputNumber, setInputNumber] = useState('');
  const [retrieved, setRetrieved] = useState(null);
  const toast = useToast();

  // Theme colors
  const bgColor = useColorModeValue('white', 'gray.800');
  const cardBg = useColorModeValue('white', 'gray.700');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const textColor = useColorModeValue('gray.600', 'gray.300');
  const headingColor = useColorModeValue('gray.700', 'white');

  // Dummy data for phone numbers
  const dummyPhoneNumbers = [
    {
      phone_number: '+1 (555) 123-4567',
      inbound_agent_id: 'agent_123',
      status: 'active',
      type: 'customer_service'
    },
    {
      phone_number: '+1 (555) 234-5678',
      inbound_agent_id: 'agent_456',
      status: 'active',
      type: 'sales'
    }
  ];

  useEffect(() => {
    setLoading(true);
    setTimeout(() => {
      setPhoneNumbers(dummyPhoneNumbers);
      setLoading(false);
    }, 1000);
  }, []);

  const createPhoneNumber = async () => {
    setLoading(true);
    try {
      await new Promise(resolve => setTimeout(resolve, 1000));
      const newNumber = {
        phone_number: '+1 (555) ' + Math.floor(Math.random() * 1000000),
        inbound_agent_id: 'agent_' + Math.floor(Math.random() * 1000),
        status: 'active',
        type: 'customer_service'
      };
      setPhoneNumbers([...phoneNumbers, newNumber]);
      toast({
        title: 'Phone number created',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      toast({
        title: 'Error creating phone number',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const retrievePhoneNumber = async () => {
    if (!inputNumber) {
      toast({
        title: 'Please enter a phone number',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setLoading(true);
    try {
      await new Promise(resolve => setTimeout(resolve, 1000));
      const found = phoneNumbers.find(p => p.phone_number === inputNumber);
      if (found) {
        setRetrieved(found);
        toast({
          title: 'Phone number found',
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
      } else {
        setRetrieved(null);
        toast({
          title: 'Phone number not found',
          status: 'warning',
          duration: 3000,
          isClosable: true,
        });
      }
    } catch (error) {
      toast({
        title: 'Error retrieving phone number',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box maxW="6xl" mx="auto" p={6}>
      <Card bg={bgColor} boxShadow="xl" borderRadius="xl" overflow="hidden">
        <CardBody p={8}>
          <VStack spacing={8} align="stretch">
            {/* Header Section */}
            <Flex justify="space-between" align="center">
              <HStack spacing={3}>
                <Icon as={FiPhone} boxSize={6} color="blue.500" />
                <Heading size="lg" color={headingColor}>Phone Numbers</Heading>
              </HStack>
              <Button
                leftIcon={<FiPlus />}
                colorScheme="blue"
                size="md"
                onClick={createPhoneNumber}
                isLoading={loading}
              >
                Add New Number
              </Button>
            </Flex>

            <Divider borderColor={borderColor} />

            {/* Stats Section */}
            <SimpleGrid columns={{ base: 1, md: 3 }} spacing={6}>
              <Card bg={cardBg} p={4} borderRadius="lg">
                <Text color={textColor} fontSize="sm" fontWeight="medium">Total Numbers</Text>
                <Text fontSize="2xl" fontWeight="bold" color={headingColor}>{phoneNumbers.length}</Text>
              </Card>
              <Card bg={cardBg} p={4} borderRadius="lg">
                <Text color={textColor} fontSize="sm" fontWeight="medium">Active Numbers</Text>
                <Text fontSize="2xl" fontWeight="bold" color={headingColor}>
                  {phoneNumbers.filter(n => n.status === 'active').length}
                </Text>
              </Card>
              <Card bg={cardBg} p={4} borderRadius="lg">
                <Text color={textColor} fontSize="sm" fontWeight="medium">Customer Service</Text>
                <Text fontSize="2xl" fontWeight="bold" color={headingColor}>
                  {phoneNumbers.filter(n => n.type === 'customer_service').length}
                </Text>
              </Card>
            </SimpleGrid>

            {/* Search Section */}
            <Box>
              <Heading size="md" mb={4} color={headingColor}>Search Phone Number</Heading>
              <InputGroup size="lg">
                <InputLeftElement pointerEvents="none">
                  <Icon as={FiSearch} color="gray.400" />
                </InputLeftElement>
                <Input
                  placeholder="Enter phone number (e.g. +14157774444)"
                  value={inputNumber}
                  onChange={(e) => setInputNumber(e.target.value)}
                  bg={cardBg}
                  borderColor={borderColor}
                />
                <Button
                  ml={2}
                  colorScheme="blue"
                  onClick={retrievePhoneNumber}
                  isLoading={loading}
                  leftIcon={<FiRefreshCw />}
                >
                  Search
                </Button>
              </InputGroup>
            </Box>

            {/* Results Section */}
            {retrieved && (
              <Card bg={cardBg} p={6} borderRadius="lg" borderWidth={1} borderColor={borderColor}>
                <VStack align="stretch" spacing={4}>
                  <HStack justify="space-between">
                    <Text fontSize="lg" fontWeight="bold" color={headingColor}>
                      {retrieved.phone_number}
                    </Text>
                    <Badge colorScheme="green" px={2} py={1} borderRadius="full">
                      {retrieved.status}
                    </Badge>
                  </HStack>
                  <Text color={textColor}>Agent ID: {retrieved.inbound_agent_id}</Text>
                  <Badge colorScheme="blue" alignSelf="start">
                    {retrieved.type.replace('_', ' ')}
                  </Badge>
                </VStack>
              </Card>
            )}

            {/* List Section */}
            <Box>
              <Heading size="md" mb={4} color={headingColor}>All Phone Numbers</Heading>
              {loading ? (
                <Center p={8}>
                  <Spinner size="xl" color="blue.500" />
                </Center>
              ) : (
                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                  {phoneNumbers.map((item, index) => (
                    <Card key={index} bg={cardBg} p={4} borderRadius="lg" borderWidth={1} borderColor={borderColor}>
                      <VStack align="stretch" spacing={3}>
                        <HStack justify="space-between">
                          <Text fontWeight="bold" color={headingColor}>{item.phone_number}</Text>
                          <Badge colorScheme="green" px={2} py={1} borderRadius="full">
                            {item.status}
                          </Badge>
                        </HStack>
                        <Text color={textColor}>Agent ID: {item.inbound_agent_id}</Text>
                        <Badge colorScheme="blue" alignSelf="start">
                          {item.type.replace('_', ' ')}
                        </Badge>
                      </VStack>
                    </Card>
                  ))}
                </SimpleGrid>
              )}
            </Box>
          </VStack>
        </CardBody>
      </Card>
    </Box>
  );
};

export default PhoneNumbers;